import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { ErrorCode } from '@thoughtspot/radiant-react/widgets/alert/alert-constants';
import { Button } from '@thoughtspot/radiant-react/widgets/button/button';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { ListTable } from '@thoughtspot/radiant-react/widgets/radiant-candidate/home-list';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useDeleteOrgMutation, useGetOrgDetailLazyQuery, useGetOrgsListQuery, } from '../../../services/org/org-service';
import { AdminPageHeader } from '../admin-page-header/admin-page-header';
import { OrgManagementTranslationKeys } from '../constants';
import { OrgDeleteModal } from './modals/org-delete-modal';
import { OrgDetailModal } from './modals/org-detail-modal';
import styles from './org-management.module.scss';
import { getOrgManagementColumns, transformOrgData, } from './org-management.utils';
import { trackOrgCreateClicked, trackOrgDeleteCancelled, trackOrgDeleteClicked, trackOrgDeleteConfirmed, trackOrgDeleteFailed, trackOrgDeleteSucceeded, trackOrgDetailsViewed, trackOrgEditClicked, trackOrgListPaginated, trackOrgListSearched, trackOrgListSorted, trackOrgTabViewed, } from './org-management-analytics';
import { OrgCreateWizard } from './wizard/org-create-wizard';
const BATCH_SIZE = 20;
const DEFAULT_LIST_INPUT = {
    record_size: BATCH_SIZE,
    record_offset: 0,
    name_pattern: '',
    sort_ascending: false,
    numgroups: 20,
};
export const OrgManagementContainer = () => {
    const { t } = useTranslation();
    const [listInput, setListInput] = useState(DEFAULT_LIST_INPUT);
    const [paginationOffset, setPaginationOffset] = useState(0);
    const [sortAscending, setSortAscending] = useState(false);
    const { data: orgDetailData, loading: orgDetailLoading, getOrgDetail, } = useGetOrgDetailLazyQuery();
    const [detailOrg, setDetailOrg] = useState(null);
    const [editOrg, setEditOrg] = useState(null);
    const [deleteOrg, setDeleteOrg] = useState(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState(null);
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [toastPrimaryAction, setToastPrimaryAction] = useState(undefined);
    const [isErrorToast, setIsErrorToast] = useState(false);
    const deleteStartTimeRef = useRef(0);
    const hasTrackedTabView = useRef(false);
    const showToast = useCallback((message, primaryAction) => {
        setToastMessage(message);
        setToastPrimaryAction(primaryAction);
        setIsErrorToast(false);
        setIsToastVisible(true);
    }, []);
    const queryInput = useMemo(() => ({
        batchsize: listInput.record_size,
        offset: paginationOffset,
        pattern: listInput.name_pattern,
        sortascending: listInput.sort_ascending,
        sort: 'CREATED',
        showinactive: false,
        numgroups: 20,
    }), [listInput, paginationOffset]);
    const { data: orgsListData, loading, refetch } = useGetOrgsListQuery(queryInput);
    const orgs = useMemo(() => {
        const rawOrgs = orgsListData?.Data?.orgs ?? [];
        const countOfUsersByOrg = orgsListData?.Data?.countOfUsersByOrg ?? {};
        const countOfGroupsByOrg = orgsListData?.Data?.countOfGroupsByOrg ?? {};
        return rawOrgs.filter(Boolean).map(org => {
            const groups = org.groups
                ?.filter(Boolean)
                .map(g => g.name)
                .filter(Boolean) ?? [];
            return {
                id: org.orgId,
                name: org.orgName,
                description: org.description || undefined,
                groups,
                totalGroups: countOfGroupsByOrg?.[org.orgId] ?? groups.length,
                users: countOfUsersByOrg?.[org.orgId] ?? 0,
                creator: {
                    id: org.author ?? undefined,
                    name: org.authorName ?? '',
                },
                createdTimestamp: org.created ?? 0,
            };
        });
    }, [orgsListData]);
    const orgsById = useMemo(() => new Map(orgs.map(o => [o.id, o])), [orgs]);
    useEffect(() => {
        if (!loading && orgsListData && !hasTrackedTabView.current) {
            hasTrackedTabView.current = true;
            trackOrgTabViewed({
                orgCount: orgs.length,
            });
        }
    }, [loading, orgsListData, orgs.length]);
    const handleOrgNameClick = useCallback((orgId) => {
        const org = orgsById.get(orgId) ?? null;
        setDetailOrg(org);
        if (org) {
            const orgAge = Math.floor((Date.now() - org.createdTimestamp * 1000) /
                (1000 * 60 * 60 * 24));
            trackOrgDetailsViewed({
                orgId: org.id,
                orgAge,
                tab: 'users',
            });
        }
    }, [orgsById]);
    const handleDetailClose = useCallback(() => setDetailOrg(null), []);
    const handleDetailEdit = useCallback(() => {
        if (detailOrg) {
            trackOrgEditClicked({
                orgId: detailOrg.id,
                fieldScope: 'profile',
            });
        }
        setEditOrg(detailOrg);
        setDetailOrg(null);
    }, [detailOrg]);
    const handleEditOpen = useCallback((orgId) => {
        trackOrgEditClicked({
            orgId,
            fieldScope: 'profile',
        });
        setEditOrg(orgsById.get(orgId) ?? null);
    }, [orgsById]);
    const handleEditClose = useCallback(() => setEditOrg(null), []);
    const handleDeleteOpen = useCallback((orgId) => {
        const org = orgsById.get(orgId) ?? null;
        if (org) {
            const orgAge = Math.floor((Date.now() - org.createdTimestamp * 1000) /
                (1000 * 60 * 60 * 24));
            trackOrgDeleteClicked({
                orgId: org.id,
                orgAge,
                memberCount: org.users,
            });
        }
        setDeleteOrg(org);
    }, [orgsById]);
    const handleDeleteClose = useCallback(() => {
        if (deleteOrg) {
            trackOrgDeleteCancelled({ orgId: deleteOrg.id });
        }
        setDeleteOrg(null);
    }, [deleteOrg]);
    const handleCreateOpen = useCallback(() => {
        trackOrgCreateClicked({ entryPoint: 'header_button' });
        setIsCreateModalOpen(true);
    }, []);
    const handleCreateClose = useCallback(() => setIsCreateModalOpen(false), []);
    const [mutateDeleteOrg, { loading: deleteOrgLoading },] = useDeleteOrgMutation(() => {
        const deletionDurationMs = Date.now() - deleteStartTimeRef.current;
        trackOrgDeleteSucceeded({
            orgId: deleteOrg?.id ?? '',
            deletionDurationMs,
        });
        setDeleteOrg(null);
        showToast(t(OrgManagementTranslationKeys.DELETE_SUCCESS), {
            label: t(OrgManagementTranslationKeys.ADD_NEW_ORG),
            onClick: handleCreateOpen,
        });
        let originRef = window.location !== window.parent.location
            ? document.referrer
            : document.location.origin;
        originRef = originRef || '*';
        window.parent.postMessage({ messageType: 'orgsListUpdated' }, originRef);
        refetch();
    }, () => {
        trackOrgDeleteFailed({
            orgId: deleteOrg?.id ?? '',
            errorMessage: 'Delete operation failed',
        });
        setDeleteOrg(null);
    }, deleteOrg?.id ?? '');
    const handleDeleteConfirm = useCallback(() => {
        if (deleteOrg) {
            trackOrgDeleteConfirmed({
                orgId: deleteOrg.id,
                confirmationMethod: 'type_name',
            });
            deleteStartTimeRef.current = Date.now();
        }
        mutateDeleteOrg();
    }, [mutateDeleteOrg, deleteOrg]);
    const handleWizardSuccess = useCallback((mode) => {
        setIsCreateModalOpen(false);
        setEditOrg(null);
        showToast(mode === 'edit'
            ? t(OrgManagementTranslationKeys.UPDATE_SUCCESS)
            : t(OrgManagementTranslationKeys.ADD_SUCCESS), {
            label: t(OrgManagementTranslationKeys.ADD_NEW_ORG),
            onClick: handleCreateOpen,
        });
        refetch();
    }, [showToast, refetch, t, handleCreateOpen]);
    const handleWizardError = useCallback((message) => {
        setToastMessage(message);
        setToastPrimaryAction(undefined);
        setIsErrorToast(true);
        setIsToastVisible(true);
    }, []);
    const onSortAscending = useCallback(() => {
        trackOrgListSorted({
            sortField: 'orgName',
            sortDirection: 'ascending',
        });
        setSortAscending(true);
        setPaginationOffset(0);
        setListInput(prev => ({
            ...prev,
            record_offset: 0,
            sort_ascending: true,
        }));
    }, []);
    const onSortDescending = useCallback(() => {
        trackOrgListSorted({
            sortField: 'orgName',
            sortDirection: 'descending',
        });
        setSortAscending(false);
        setPaginationOffset(0);
        setListInput(prev => ({
            ...prev,
            record_offset: 0,
            sort_ascending: false,
        }));
    }, []);
    const onSearchChange = useCallback((term) => {
        trackOrgListSearched({
            queryLength: term.length,
            resultCount: orgs.length,
        });
        setPaginationOffset(0);
        setListInput(prev => ({
            ...prev,
            record_offset: 0,
            name_pattern: term || '',
        }));
    }, [orgs.length]);
    const actionCallbacks = useMemo(() => ({
        onEdit: handleEditOpen,
        onDelete: handleDeleteOpen,
    }), [handleEditOpen, handleDeleteOpen]);
    const tableData = useMemo(() => transformOrgData(orgs, actionCallbacks, t), [orgs, actionCallbacks, t]);
    const columns = useMemo(() => getOrgManagementColumns(sortAscending, onSortAscending, onSortDescending, handleOrgNameClick, t), [
        sortAscending,
        onSortAscending,
        onSortDescending,
        handleOrgNameClick,
        t,
    ]);
    const isLastBatch = orgsListData?.Data?.isLastBatch ?? true;
    const previous = useCallback(() => setPaginationOffset(prev => {
        const newOffset = prev - BATCH_SIZE;
        trackOrgListPaginated({
            pageNumber: Math.floor(newOffset / BATCH_SIZE) + 1,
            pageSize: BATCH_SIZE,
        });
        return newOffset;
    }), []);
    const next = useCallback(() => setPaginationOffset(prev => {
        const newOffset = prev + BATCH_SIZE;
        trackOrgListPaginated({
            pageNumber: Math.floor(newOffset / BATCH_SIZE) + 1,
            pageSize: BATCH_SIZE,
        });
        return newOffset;
    }), []);
    const jumpTo = useCallback((pageIndex) => {
        trackOrgListPaginated({
            pageNumber: pageIndex + 1,
            pageSize: BATCH_SIZE,
        });
        setPaginationOffset(pageIndex * BATCH_SIZE);
    }, []);
    const paginationConfig = useMemo(() => orgs.length === 0
        ? undefined
        : {
            offset: paginationOffset,
            batchSize: BATCH_SIZE,
            previous,
            next,
            jumpTo,
            fetch: {
                offset: paginationOffset,
                batchSize: BATCH_SIZE,
                objectsCount: orgs.length,
                isLastBatch,
                previous: () => { },
                next: () => { },
            },
        }, [paginationOffset, orgs.length, isLastBatch, previous, next, jumpTo]);
    const noDataProps = useMemo(() => ({
        title: t(OrgManagementTranslationKeys.NO_ORGS_FOUND),
        description: listInput.name_pattern
            ? t(OrgManagementTranslationKeys.NO_RESULTS_MATCH_SEARCH)
            : t(OrgManagementTranslationKeys.NO_ORGS_TO_DISPLAY),
        img: ErrorCode.GLOBAL_GENERAL_WARNING,
    }), [listInput.name_pattern, t]);
    const searchConfig = useMemo(() => ({
        value: listInput.name_pattern ?? '',
        placeholder: t(OrgManagementTranslationKeys.SEARCH_PLACEHOLDER),
        onChange: onSearchChange,
    }), [listInput.name_pattern, onSearchChange]);
    return (React.createElement(Vertical, { className: styles.orgManagementContainer, "data-testid": "org-management-container" },
        React.createElement(AdminPageHeader, { title: t(OrgManagementTranslationKeys.PAGE_TITLE), titleTestId: "org-management-title" }),
        React.createElement(LoadingIndicator.Global, { hideLoader: !loading },
            React.createElement(Vertical, { className: styles.tableWrapper },
                React.createElement(ListTable, { columns: columns, data: tableData, loading: loading, search: searchConfig, pagination: paginationConfig, noDataProps: noDataProps, "data-testid": "org-management-list-table", suppressRowVirtualisation: true, suppressCellVirtualisation: true }),
                React.createElement(Horizontal, { className: styles.actionBarSplit },
                    React.createElement(Button, { type: "primary", text: t(OrgManagementTranslationKeys.ADD_NEW_ORG), onClick: handleCreateOpen, "data-testid": "add-new-org-button" })))),
        React.createElement(OrgDetailModal, { isOpen: !!detailOrg, org: detailOrg, orgDetailData: orgDetailData, orgDetailLoading: orgDetailLoading, onGetOrgDetail: getOrgDetail, onEdit: handleDetailEdit, onClose: handleDetailClose }),
        React.createElement(OrgCreateWizard, { isOpen: isCreateModalOpen, onClose: handleCreateClose, onSuccess: handleWizardSuccess, onError: handleWizardError }),
        React.createElement(OrgCreateWizard, { isOpen: !!editOrg, org: editOrg, onClose: handleEditClose, onSuccess: handleWizardSuccess, onError: handleWizardError }),
        deleteOrg && (React.createElement(OrgDeleteModal, { isOpen: true, orgName: deleteOrg.name, isLoading: deleteOrgLoading, onConfirm: handleDeleteConfirm, onClose: handleDeleteClose })),
        React.createElement(GlobalAlert.Toast, { isVisible: isToastVisible, message: toastMessage, onClose: () => setIsToastVisible(false), primaryAction: toastPrimaryAction, autoHide: true, showCheckmarkIcon: !isErrorToast })));
};
//# sourceMappingURL=org-management.container.js.map